/**
 * Perks, as the same numbers gear already produces.
 *
 * This is the whole point of the framework. `aggregateLoadout` turns equipment into a `Loadout`,
 * and this turns perks into one, and `combineLoadouts` adds them up. There is no perk-shaped hole
 * anywhere downstream, no second modifier system, and no consumer that has to ask which of the two
 * a number came from. `Loadout` keeps living in `sim/inventory` because that is where the first
 * producer of one was written; it describes a character, not a backpack.
 *
 * The moment is a parameter rather than state, so this is a pure function of a build and an
 * instant. A conditional perk contributes its identity when its condition does not hold, never a
 * branch that skips it, which keeps the sum the same length whatever is true at the time.
 *
 * Summing in `PERK_ORDER` is part of the determinism contract, because floating point
 * multiplication is not associative and two peers must agree on the last bit.
 */

import { EMPTY_EFFECTS, combineEffects, effectFrom } from '@/sim/effects/characterEffects';
import { PERK_ORDER, perk } from '@/sim/progression/perkRegistry';
import { STAT_ORDER } from '@/sim/combat/statContract';
import { conditionHolds } from '@/sim/effects/effectConditionRegistry';
import { rankOf } from '@/sim/progression/progressState';
import type { CharacterMoment } from '@/sim/effects/characterMoment';
import type { DamageModifier } from '@/sim/combat/damageResolution';
import type { EffectBundle } from '@/sim/effects/characterEffects';
import type { Loadout, StatDeltas } from '@/sim/inventory/loadoutStats';
import type { ProgressState } from '@/sim/progression/progressState';

/**
 * Every perk taken, added up, as of one moment. The one function perks reach the combat model
 * through, and the mirror of `aggregateLoadout`.
 */
export function aggregatePerks(progress: ProgressState, moment: CharacterMoment): Loadout {
  const statDeltas: StatDeltas = {};
  const damageModifiers: DamageModifier[] = [];
  let effects: EffectBundle = EMPTY_EFFECTS;

  for (const id of PERK_ORDER) {
    const rank = rankOf(progress, id);

    if (rank <= 0) continue;

    const def = perk(id);

    // Walked in stat order rather than by object key, so the sum is the same on every machine
    // and nothing has to be cast back out of an entry pair.
    for (const stat of STAT_ORDER) {
      const grant = def.statGrants[stat];

      if (grant === undefined || !Number.isFinite(grant)) continue;
      statDeltas[stat] = (statDeltas[stat] ?? 0) + grant * rank;
    }

    for (const effect of def.effects) {
      if (!conditionHolds(effect.condition, moment)) continue;
      effects = combineEffects(effects, effectFrom(effect.field, effect.perRank * rank));
    }

    // One copy per rank, so a rank compounds the way two separate sources of the same modifier
    // would. The pipeline decides what a modifier does; this only decides how many there are.
    for (let taken = 0; taken < rank; taken += 1) damageModifiers.push(...def.damageModifiers);
  }

  return { statDeltas, armor: { threshold: 0, rating: 0 }, damageModifiers, effects };
}
